// Project Integration Code Modal - Zeigt Code für Formular-Integration

import { useState } from 'react';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import type { Project } from '@/lib/types';
import toast from 'react-hot-toast';

interface ProjectIntegrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  project: Project | null;
}

const API_URL = 'https://dashboard.nico-els.de/api/leads';
const SUPABASE_URL = 'https://fuxdkfjqzcszkxejndgw.supabase.co';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImZ1eGRrZmpxemNzemt4ZWpuZGd3Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3Njk5MjI4NTcsImV4cCI6MjA4NTQ5ODg1N30.DmTZRcnCP2htCJ_HxEagvUWjhYCETdwo_cdXHkF1Jow';

export default function ProjectIntegrationModal({ isOpen, onClose, project }: ProjectIntegrationModalProps) {
  const [activeTab, setActiveTab] = useState<'prompt' | 'code'>('prompt');

  if (!project) return null;

  const customerName = project.customer?.name || 'Kunde';

  // Cursor Prompt für AI-Integration
  const cursorPrompt = `Ich möchte dieses Kontaktformular mit meinem Lead-Management-System verbinden.

## API Details

**API Endpoint:** ${API_URL}
**Kunde:** ${customerName}
**Projekt:** ${project.name}
**Project Slug:** ${project.slug}

## Datei-Upload zu Supabase Storage

Falls das Formular Datei-Uploads hat, müssen die Dateien zuerst zu Supabase Storage hochgeladen werden:

**Supabase URL:** ${SUPABASE_URL}
**Supabase Anon Key:** ${SUPABASE_ANON_KEY}
**Storage Bucket:** lead-files

### Datei-Upload Funktion:

\`\`\`javascript
// Datei zu Supabase Storage hochladen
async function uploadFile(file) {
  const fileName = \`\${Date.now()}-\${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}\`;
  const filePath = \`${project.slug}/\${fileName}\`;
  
  const response = await fetch(
    \`${SUPABASE_URL}/storage/v1/object/lead-files/\${filePath}\`,
    {
      method: 'POST',
      headers: {
        'Authorization': \`Bearer ${SUPABASE_ANON_KEY}\`,
        'Content-Type': file.type,
      },
      body: file
    }
  );
  
  if (!response.ok) {
    throw new Error('Datei-Upload fehlgeschlagen');
  }
  
  // Bucket ist PRIVAT: nur den Storage-Pfad speichern.
  // Dashboard und Lead-Mail signieren beim Zugriff selbst.
  return {
    file_name: file.name,
    file_url: filePath
  };
}

// Mehrere Dateien hochladen
async function uploadFiles(files) {
  const uploadedFiles = [];
  for (const file of files) {
    const result = await uploadFile(file);
    uploadedFiles.push(result);
  }
  return uploadedFiles;
}
\`\`\`

## Formular-Submission

\`\`\`javascript
async function submitForm(formData, files = []) {
  // 1. Dateien hochladen (falls vorhanden)
  let uploadedFiles = [];
  if (files && files.length > 0) {
    uploadedFiles = await uploadFiles(files);
  }
  
  // 2. Lead an API senden
  const response = await fetch('${API_URL}', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      customer_slug: '${project.slug}',
      form_identifier: 'kontaktformular', // z.B. 'kontaktformular', 'angebotsanfrage'
      contact_info: {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || undefined
      },
      fields: {
        // ALLE anderen Formularfelder hier
        nachricht: formData.nachricht,
        // weitere_felder: formData.weitereFelder,
      },
      field_labels: {
        // Deutsche Bezeichnungen für Dashboard und E-Mail
        nachricht: 'Ihre Nachricht',
        // weitere_felder: 'Weitere Felder',
      },
      files: uploadedFiles // Array mit {file_name, file_url}
    })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Fehler beim Senden');
  }

  return await response.json();
}
\`\`\`

## WICHTIG

- **customer_slug** ist fest auf "${project.slug}" eingestellt
- **form_identifier** ist frei wählbar (z.B. 'kontaktformular', 'bewerbung')
- **fields** kann beliebige Felder enthalten - das System ist schema-agnostisch
- **field_labels** sind die deutschen Bezeichnungen für E-Mail und Dashboard
- **files** ist ein Array mit Objekten: { file_name: "name.pdf", file_url: "${project.slug}/<datei>" } — file_url trägt den **Storage-Pfad**, keine öffentliche URL (der Bucket ist privat; Dashboard und Lead-Mail signieren beim Zugriff)
- Erlaubte Dateitypen: JPEG, PNG, GIF, WebP, PDF, DOC, DOCX (max. 10MB)

Bitte passe das bestehende Kontaktformular entsprechend an. Die Dateien werden automatisch im Dashboard angezeigt und sind dort herunterladbar.`;

  // Fertiger JavaScript Code
  const javascriptCode = `// ===================================================
// Lead-Management Integration für: ${project.name}
// Kunde: ${customerName}
// Project Slug: ${project.slug}
// ===================================================

const LEAD_API_URL = '${API_URL}';
const SUPABASE_URL = '${SUPABASE_URL}';
const SUPABASE_ANON_KEY = '${SUPABASE_ANON_KEY}';
const PROJECT_SLUG = '${project.slug}';

// Einzelne Datei hochladen
async function uploadFileToStorage(file) {
  const fileName = \`\${Date.now()}-\${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}\`;
  const filePath = \`\${PROJECT_SLUG}/\${fileName}\`;
  
  const response = await fetch(
    \`\${SUPABASE_URL}/storage/v1/object/lead-files/\${filePath}\`,
    {
      method: 'POST',
      headers: {
        'Authorization': \`Bearer \${SUPABASE_ANON_KEY}\`,
        'Content-Type': file.type,
      },
      body: file
    }
  );
  
  if (!response.ok) {
    throw new Error(\`Datei-Upload fehlgeschlagen: \${file.name}\`);
  }
  
  // Bucket ist PRIVAT: nur den Storage-Pfad speichern.
  // Dashboard und Lead-Mail signieren beim Zugriff selbst.
  return {
    file_name: file.name,
    file_url: filePath
  };
}

// Mehrere Dateien hochladen
async function uploadFiles(files) {
  if (!files || files.length === 0) return [];
  
  const uploadedFiles = [];
  for (const file of Array.from(files)) {
    try {
      const result = await uploadFileToStorage(file);
      uploadedFiles.push(result);
    } catch (error) {
      console.error('Fehler beim Upload:', file.name, error);
    }
  }
  return uploadedFiles;
}

// Hauptfunktion: Formular absenden
async function submitToLeadManagement(formData, fileInput = null) {
  try {
    // 1. Dateien hochladen (falls vorhanden)
    let uploadedFiles = [];
    if (fileInput && fileInput.files && fileInput.files.length > 0) {
      uploadedFiles = await uploadFiles(fileInput.files);
    }
    
    // 2. Lead an API senden
    const response = await fetch(LEAD_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        customer_slug: PROJECT_SLUG,
        form_identifier: 'kontaktformular', // ← Anpassen!
        contact_info: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone || undefined
        },
        fields: {
          // Füge hier alle zusätzlichen Felder ein:
          nachricht: formData.nachricht,
          // budget: formData.budget,
          // projekttyp: formData.projekttyp,
        },
        field_labels: {
          nachricht: 'Ihre Nachricht',
          // budget: 'Budget',
          // projekttyp: 'Projekttyp',
        },
        files: uploadedFiles
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Fehler beim Senden');
    }

    const data = await response.json();
    return { success: true, leadId: data.lead_id };
    
  } catch (error) {
    console.error('Lead-Übermittlung fehlgeschlagen:', error);
    return { success: false, error: error.message };
  }
}

// Verwendung:
// const fileInput = document.querySelector('input[type="file"]');
// const result = await submitToLeadManagement(formData, fileInput);
// if (result.success) {
//   console.log('Lead erstellt:', result.leadId);
// }`;

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} kopiert!`);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Integration: ${project.name}`}
      size="lg"
    >
      <div className="space-y-4">
        {/* Info Box */}
        <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-4">
          <p className="text-sm text-neutral-700">
            <strong>Project Slug:</strong> <code className="bg-neutral-200 px-2 py-0.5 rounded text-sm">{project.slug}</code>
          </p>
          <p className="text-sm text-neutral-500 mt-1">
            Leads werden automatisch dem Kunden <strong>{customerName}</strong> zugeordnet.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-neutral-200">
          <button
            onClick={() => setActiveTab('prompt')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'prompt'
                ? 'border-neutral-900 text-neutral-900'
                : 'border-transparent text-neutral-500 hover:text-neutral-700'
            }`}
          >
            Cursor Prompt
          </button>
          <button
            onClick={() => setActiveTab('code')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'code'
                ? 'border-neutral-900 text-neutral-900'
                : 'border-transparent text-neutral-500 hover:text-neutral-700'
            }`}
          >
            JavaScript Code
          </button>
        </div>

        {/* Content */}
        <div>
          {activeTab === 'prompt' && (
            <div>
              <p className="text-sm text-neutral-600 mb-2">
                Kopieren Sie diesen Prompt und fügen Sie ihn bei Cursor im anderen Projekt ein:
              </p>
              <pre className="bg-neutral-900 text-neutral-100 p-4 rounded-lg text-xs overflow-x-auto max-h-96 overflow-y-auto">
                {cursorPrompt}
              </pre>
              <Button
                className="mt-3 w-full"
                onClick={() => copyToClipboard(cursorPrompt, 'Cursor Prompt')}
              >
                Cursor Prompt kopieren
              </Button>
            </div>
          )}

          {activeTab === 'code' && (
            <div>
              <p className="text-sm text-neutral-600 mb-2">
                Fertiger JavaScript Code zum Einbinden:
              </p>
              <pre className="bg-neutral-900 text-green-400 p-4 rounded-lg text-xs overflow-x-auto max-h-96 overflow-y-auto">
                {javascriptCode}
              </pre>
              <Button
                className="mt-3 w-full"
                onClick={() => copyToClipboard(javascriptCode, 'JavaScript Code')}
              >
                JavaScript Code kopieren
              </Button>
            </div>
          )}
        </div>

        {/* Footer Info */}
        <div className="bg-neutral-50 rounded-lg p-3 text-xs text-neutral-600">
          <strong>Datei-Upload:</strong> Dateien werden automatisch zu Supabase Storage hochgeladen 
          und sind dann im Dashboard herunterladbar. Erlaubt: JPEG, PNG, GIF, WebP, PDF, DOC (max. 10MB)
        </div>
      </div>
    </Modal>
  );
}
